import * as Yup from 'yup';
import { Grid, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { AssociatedTeamMember, DataRoomUser } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { QueryKeys, useBankContactTeamMembersQuery } from '../../../shared/hooks/Queries';
import { AvailableTeamMemberList } from './AvailableTeamMemberList';
import React from 'react';

interface AddTeamMemberFormProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null | undefined;
	isBankUser: boolean;
	close: () => void;
}

export const AddTeamMemberForm: React.FC<AddTeamMemberFormProps> = ({ dataRoomGuid, user, isBankUser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const teamMembersQuery = useBankContactTeamMembersQuery(dataRoomGuid, user?.guid, {
		enabled: user != null,
		retry: false,
	});

	const addTeamMemberMutation = useMutation(
		async ({ values }: AddTeamMemberFormMutationVariables) => {
			const teamMemberPatch = {
				firstName: values.firstName,
				lastName: values.lastName,
				emailAddress: values.emailAddress,
				phone: values.phone,
				editUnderwritingCriteria: values.editUnderwritingCriteria,
				viewUnderwritingCriteria: values.viewUnderwritingCriteria,
				canInteractWithTeasers: values.canInteractWithTeasers,
				sendInvite: values.sendInvite,
			};

			const { data, error } = await getApiResponseData<AssociatedTeamMember>(
				api.post(CerebroApiEndpoints.DataRoomUsers.PostTeamMember(dataRoomGuid, user?.guid), teamMemberPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			if (data != null) {
				user?.teamMembers.push(data);
			}

			return data;
		},
		{
			onSuccess: () => {
				Promise.all([
					client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.LenderByUser(dataRoomGuid, user?.userGuid)),
				]).finally(() => {
					snackbar.enqueueSnackbar('Team member was added to data room.', { variant: 'success' });
					close();
				});
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>
				{user?.firstName} {user?.lastName}
			</CerebroHeading>
			<CerebroText variant="large" sx={{ fontWeight: 'bold' }}>
				{user?.bankName}
			</CerebroText>

			{teamMembersQuery.isLoading && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}

			<CerebroText variant="small" sx={{ marginTop: 2 }}>
				An email with details for accessing the Cerebro system will be sent to the team member being added. The
				Associate button will not show for inactive team members.
			</CerebroText>

			{teamMembersQuery.isSuccess && (
				<AvailableTeamMemberList
					dataRoomGuid={dataRoomGuid}
					user={user}
					teamMembers={teamMembersQuery.data}
					close={close}
				/>
			)}

			<Formik
				validationSchema={AddTeamMemberSchema}
				initialValues={{
					firstName: '',
					lastName: '',
					emailAddress: '',
					phone: '',
					editUnderwritingCriteria: true,
					viewUnderwritingCriteria: true,
					canInteractWithTeasers: true,
					sendInvite: true,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(addTeamMemberMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="firstName" required>
										First Name
									</CerebroLabel>
									<Field
										id="firstName"
										as={CerebroInput}
										name="firstName"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('firstName', errors, touched)}
									/>
									{FormikUtils.IsInvalid('firstName', errors, touched) && (
										<CerebroText variant="error">{errors.firstName}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="lastName" required>
										Last Name
									</CerebroLabel>
									<Field
										id="lastName"
										as={CerebroInput}
										name="lastName"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('lastName', errors, touched)}
									/>
									{FormikUtils.IsInvalid('lastName', errors, touched) && (
										<CerebroText variant="error">{errors.lastName}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="emailAddress" required>
										Email Address
									</CerebroLabel>
									<Field
										id="emailAddress"
										as={CerebroInput}
										name="emailAddress"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('emailAddress', errors, touched)}
									/>
									{FormikUtils.IsInvalid('emailAddress', errors, touched) && (
										<CerebroText variant="error">{errors.emailAddress}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="phone">Phone</CerebroLabel>
									<Field id="phone" as={CerebroInput} name="phone" disabled={isSubmitting} />
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="editUnderwritingCriteria">
										Edit Underwriting Criteria
									</CerebroLabel>
									<Field
										id="editUnderwritingCriteria"
										as={CerebroYesNo}
										name="editUnderwritingCriteria"
										disabled={isSubmitting}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="viewUnderwritingCriteria">
										View Underwriting Criteria
									</CerebroLabel>
									<Field
										id="viewUnderwritingCriteria"
										as={CerebroYesNo}
										name="viewUnderwritingCriteria"
										disabled={isSubmitting}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="canInteractWithTeasers">Can Interact with Teasers</CerebroLabel>
									<Field
										id="canInteractWithTeasers"
										as={CerebroYesNo}
										name="canInteractWithTeasers"
										disabled={isSubmitting}
									/>
								</Grid>

								{!isBankUser && (
									<Grid item xs={8}>
										<CerebroLabel id="sendInvite">Send Invite</CerebroLabel>
										<Field
											id="sendInvite"
											as={CerebroYesNo}
											name="sendInvite"
											disabled={isSubmitting}
										/>
									</Grid>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddTeamMemberFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddTeamMemberFormValues>;
interface AddTeamMemberFormValues {
	firstName: string;
	lastName: string;
	emailAddress: string;
	phone: string;
	editUnderwritingCriteria: boolean;
	viewUnderwritingCriteria: boolean;
	canInteractWithTeasers: boolean;
	sendInvite: boolean;
}

const AddTeamMemberSchema = Yup.object().shape({
	firstName: Yup.string().required('First name is required'),
	lastName: Yup.string().required('Last name is required'),
	emailAddress: Yup.string().email('invalid emaill address').required('email address is required'),
});
